import {useTranslation} from "react-i18next";
import CommonClass from "@/utils/common";
import clsx from "clsx";
import st from "@/styles/CreditItem.module.scss";
import Button from "@/ui/Button.tsx";
import DashboardClass from "@/utils/dashboard_class";
import {useNavigate} from 'react-router-dom';
import useCommon from "@/hooks/common.ts";
import CrLineTransactionInterface from "@/types_and_interfaces/cr_line_transaction_interface.ts";
import React, {useEffect, useRef, useState} from "react";
import useLoans from "@/hooks/loans.ts";
import {useAtom} from "jotai/index";
import {cr_line_contract} from "@/atoms/common.ts";
import LoanInterface from "@/types_and_interfaces/loan_interface.ts";
import {CircularProgress} from "@mui/joy";

interface ItemProps {
    obj: CrLineTransactionInterface
    cr_line_item?:boolean
    cr_line: LoanInterface,
    is_main_page?:boolean,
    is_cr_line_page?:boolean
}


const CreditLineItem:React.FC<ItemProps> = ({obj, cr_line, is_cr_line_page, is_main_page}) => {
    
    const {
        amount,
        client_loan_id,
        created_at,
        status,
        status_simplified,
        urn_tr,
        // pay_date
        fully_paid,
    } = obj
    
    const restant = cr_line.restant_days > 0
    const [contract_atom] = useAtom(cr_line_contract)
    const is_contract = contract_atom.hasOwnProperty(urn_tr)
    const [contract_enable, set_contract_enable] = useState<boolean | undefined>(undefined)
    const [contract_error, /*set_contract_error*/] = useState<boolean>(false)

    const effect_ran = useRef<boolean>(false)
    
    const {t} = useTranslation()
    const navigate = useNavigate();
    const common_hook = useCommon()
    const loans = useLoans()
    const date = status_simplified === "approved" ? cr_line.pay_date.replace(/\//g, '.') : common_hook.formatMomentDate(created_at)
    
    const data_row = [
        {
            label: t("amount"),
            value: CommonClass.formatMoneyAmount(Number(amount)) + " " + t("currency"),
            copy: false,
            visible: true
        },
        {
            label: status_simplified === "approved" ? t( fully_paid ? "last_payment" : "payment_date" ) : t("date"),
            value: date ,
            copy: false,
            visible: Boolean(date),
        },
        {
            label: t("transaction_nr"),
            value: urn_tr,
            copy: true,
            visible: !is_cr_line_page
        },

    ]
    
    
    useEffect(() => {
        
        if(status_simplified === "sign") {
            
            
            const upd = async () => {
                let is_contract = await loans.prefetch_transaction_contract({
                    client_loan_id: client_loan_id,
                    urn: urn_tr
                }, urn_tr)


                if(!is_contract) {

                    let count = 0

                    const interval = setInterval(async () => {
                        count+=1

                        is_contract = await loans.prefetch_transaction_contract({
                            client_loan_id: client_loan_id,
                            urn: urn_tr
                        } , urn_tr)

                        set_contract_enable(is_contract)
                        
                        if(is_contract /*|| count > max_count*/) {
                            clearInterval(interval)

                            // if(count > max_count){
                            //     set_contract_error(true)
                            // }
                        }

                    }, 5000)

                } else {
                    set_contract_enable(is_contract)
                }
                
            }
            
            if(!effect_ran.current) {
                upd()
                
            }
        }
        
        effect_ran.current = true
    }, []);

    return (
        <div
            className={clsx(
                st.credit_item,
                !!is_cr_line_page && !is_main_page && st.transaction_sign,
                (status_simplified === "approved" || status_simplified === "sign") && st.accepted,
                status_simplified === "pending" && st.pending,
                status_simplified === "denied" && st.error,
                status_simplified === "application_err" && st.error,
                status_simplified === "rejected" && st.error,
                restant && st.restant,
                fully_paid && st.paid
            )}
        >
            <div className={st.top}>
                {!!is_cr_line_page && !is_main_page ? (
                    <div className={st.top_left}>
                        <strong>{`${t("transaction_sign_nr")} ${urn_tr}`}</strong>
                    </div>
                ) : status_simplified === "approved" ? (
                    <>
                        <div className={st.top_left}>
                            <strong>
                                {/*{product_group}*/}
                            </strong>
                            
                            <span>
                                {t("requested")} {common_hook.formatMomentDate(created_at)}
                            </span>
                        </div>
                        
                        <div className={st.top_right}>
                            
                            {restant  ? (
                                <></>
                            ) : (
                                <div style={{textTransform:"capitalize"}} className={clsx(st.status_badge)}>
                                    { fully_paid ? t("paid") : t("active")}
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <div className={st.top_left}>

                        {status_simplified === "pending" && (
                            <strong>
                                {t("pending")}
                            </strong>
                        )}

                        {status_simplified === "sign" && (
                            <strong>
                                {t("approved")}
                            </strong>
                        )}

                        {status_simplified === "application_err" && (
                            <strong>
                                {t(status)}
                            </strong>
                        )}

                        {status_simplified === "denied" && (
                            <strong>
                                {t("denied")}
                            </strong>
                        )}

                        {status_simplified === "rejected" && (
                            <strong>
                                {t("rejected")}
                            </strong>
                        )}
                    </div>
                )}
            </div>
            
            <div className={st.center}>
                {data_row.map((data_obj, i) => {
                    
                    const {label, value, copy, visible} = data_obj
                    
                    return visible && (
                        <div
                            key={`data_obj_${i}`}
                            className={st.data_obj}
                        >
                            <span>
                                {label}
                            </span>
                            
                            <strong
                                onClick={() => {
                                    if(copy) {
                                        common_hook.copyToClipboard(value)
                                    }
                                }}
                                style={{
                                    textDecoration: copy ?  "underline" : undefined,
                                    cursor: copy ?  "pointer" : undefined,
                                }}
                            >
                                {value}
                            </strong>

                        </div>
                    )
                })}
            </div>
            
            
            {status_simplified === "sign" && (
                <Button
                    onClick={() => {
                        navigate(DashboardClass.dashboard_routes.SignCrLineContract +"/"+urn_tr)
                    }}
                    disabled={status_simplified === "sign" && !is_contract}
                    className={clsx(st.btn)}
                >
                    {(status_simplified === "sign" && !is_contract && !contract_error) && (
                        <CircularProgress color={"neutral"} size={"sm"} />
                    )}
                    {t("sign_contract_part")}
                </Button>
            )}
            
            {(contract_enable === false && status_simplified === "sign")  && (
                <span className={clsx(st.sm_text, contract_error ? st.danger : st.warn)}>
                    {contract_error ? t("contract_err") : t("contract_is_pending")}
                </span>
            )}
        </div>
    )
}

export default CreditLineItem